import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

type Props = {
  title: string;
  description?: string;
  icon?: LucideIcon;
  ctaLabel?: string;
  ctaTo?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  className?: string;
};

export function EmptyState({ title, description, icon: Icon, ctaLabel, ctaTo, ctaHref, onCtaClick, className }: Props) {
  return (
    <div className={`card-soft p-10 text-center max-w-xl mx-auto ${className ?? ""}`}>
      {Icon && <Icon className="mx-auto mb-3 text-muted-foreground" size={28} aria-hidden="true" />}
      <h3 className="font-display font-semibold mb-1 text-foreground">{title}</h3>
      {description && <p className="text-sm text-muted-foreground mb-5">{description}</p>}
      {ctaLabel && (ctaTo ? (
        <Link to={ctaTo} className="btn-pill !text-sm">{ctaLabel}</Link>
      ) : ctaHref ? (
        <a href={ctaHref} target="_blank" rel="noreferrer" className="btn-pill !text-sm">{ctaLabel}</a>
      ) : (
        <button onClick={onCtaClick} className="btn-pill !text-sm">{ctaLabel}</button>
      ))}
    </div>
  );
}

export function CardSkeletonGrid({ count = 6, className }: { count?: number; className?: string }) {
  return (
    <div className={className ?? "grid md:grid-cols-2 lg:grid-cols-3 gap-5"}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card-soft p-6 animate-pulse">
          <div className="h-7 w-7 rounded bg-primary/10 mb-3" />
          <div className="h-4 w-2/3 rounded bg-primary/10 mb-2" />
          <div className="h-3 w-full rounded bg-primary/10 mb-1" />
          <div className="h-3 w-4/5 rounded bg-primary/10 mb-4" />
          <div className="h-8 w-28 rounded-full bg-primary/10" />
        </div>
      ))}
    </div>
  );
}

export function RowSkeleton({ rows = 5, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-t border-border">
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c} className="px-4 py-4">
              <div className="h-3.5 rounded bg-primary/10 animate-pulse" style={{ width: `${50 + ((r + c) % 4) * 12}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
